import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Plug, Loader2, CheckCircle2, Circle, X, Trash2, Save,
  Mail, Phone, MessageCircle, Slack, CreditCard, Sparkles, Globe, Briefcase,
  Github, Cloud, Server, Send, MessageSquare, Mic, Hash, Calendar, Workflow, BrainCircuit,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import OAuthConnectPanel from '../components/connections/OAuthConnectPanel';
import ConnectionSetupGuide from '../components/connections/ConnectionSetupGuide';
import { CONNECTIONS_PAGE_INTRO } from '../data/connectionSetupGuides';
import { getOAuthProviderMeta } from '../data/oauthProvidersMeta';
import {
  CATEGORY_ORDER, CONNECTION_TONES, getProviderMeta, mergeProviderCatalog, resolveProviderEntry,
  STATIC_CONNECTION_PROVIDERS,
} from '../data/connectionProvidersMeta';
import {
  extractPipedreamRedirectError,
  formatPipedreamConnectError,
} from '../utils/pipedreamConnect';

/** Hide underlying OAuth vendor brand in category labels shown on /connections. */
function displayCategoryLabel(category) {
  if (!category) return category;
  if (category === 'Pipedream Connect' || /^pipedream$/i.test(category)) return 'Managed OAuth';
  return category;
}

/** Strip vendor brand from catalog copy that reaches the Connections UI. */
function scrubConnectionCopy(text) {
  if (!text) return text;
  return String(text)
    .replace(/\s*from Pipedream\.?/gi, '.')
    .replace(/Pipedream Connect/gi, 'Managed OAuth')
    .replace(/\bPipedream\b/gi, 'managed OAuth')
    .replace(/\.\./g, '.');
}

function scrubProviderForDisplay(provider) {
  if (!provider) return provider;
  return {
    ...provider,
    category: displayCategoryLabel(provider.category),
    description: scrubConnectionCopy(provider.description),
  };
}

const ICON_MAP = {
  Mail, Phone, MessageCircle, Slack, CreditCard, Sparkles, Globe, Briefcase,
  Github, Cloud, Server, Send, MessageSquare, Mic, Hash, Calendar, Plug, Workflow,
};

/** Deep-link targets from Co-founder readiness (?focus=social / ?focus=email). */
const SOCIAL_FOCUS_PROVIDER_IDS = new Set([
  'facebook', 'instagram', 'twitter', 'twitter-v2', 'twitter-oauth2-cc',
  'linkedin', 'tiktok', 'buffer', 'hootsuite', 'threads', 'pinterest', 'x',
]);
const FOCUS_TO_CATEGORY = {
  email: 'Email',
  smtp: 'Email',
  sendgrid: 'Email',
};

const brandSlugFor = (providerId, meta) => {
  if (meta && meta.brand) return meta.brand;
  return String(providerId || '')
    .replace(/-(oauth|mcp|app|webhook)$/i, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
};

const ProviderIcon = ({ providerId, className = 'w-5 h-5' }) => {
  const meta = getProviderMeta(providerId);
  const Icon = ICON_MAP[meta.icon] || Plug;
  const slug = brandSlugFor(providerId, meta);
  const [imgFailed, setImgFailed] = useState(false);
  if (slug && !imgFailed) {
    const url = `https://cdn.simpleicons.org/${slug}${meta.light ? '/ffffff' : ''}`;
    return (
      <img
        src={url}
        alt=""
        aria-hidden="true"
        draggable={false}
        onError={() => setImgFailed(true)}
        className={className}
        style={{ objectFit: 'contain' }}
      />
    );
  }
  return <Icon className={className} />;
};

const ConnectionBlock = ({ provider, connection, onConfigure }) => {
  const meta = getProviderMeta(provider.id);
  const tone = CONNECTION_TONES[meta.tone] || CONNECTION_TONES.zinc;
  const connected = Boolean(connection);
  const connectVia = provider.connect_via
    || (provider.auth_type === 'oauth2' ? 'byoc' : 'byok');
  const methodBadge = {
    pipedream: ['border-violet-500/40 bg-violet-500/10 text-violet-200', 'Managed OAuth'],
    platform: ['border-emerald-500/40 bg-emerald-500/10 text-emerald-200', 'Platform OAuth'],
    byoc: ['border-sky-500/40 bg-sky-500/10 text-sky-200', 'BYOC'],
    byok: ['border-amber-500/40 bg-amber-500/10 text-amber-200', 'API key / BYOK'],
  }[connectVia] || ['border-zinc-700 text-zinc-400', connectVia];

  return (
    <button
      type="button"
      onClick={() => onConfigure(provider, connection)}
      data-testid={`conn-block-${provider.id}`}
      className={`group relative text-left w-full bg-gradient-to-br ${tone.bg} to-zinc-900/90 border ${tone.border} ${tone.borderHover} rounded-2xl p-4 transition-all hover:scale-[1.01] hover:shadow-lg hover:shadow-black/20`}
    >
      {connected ? (
        <span className={`absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${tone.badge}`}>
          <CheckCircle2 className="w-3 h-3" /> Connected
        </span>
      ) : (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-zinc-700 text-zinc-500">
          <Circle className="w-3 h-3" /> Not set up
        </span>
      )}
      <span className={`absolute top-3 left-3 inline-flex px-2 py-0.5 rounded-full text-[9px] font-medium border ${methodBadge[0]}`}>
        {methodBadge[1]}
      </span>
      <div className={`w-11 h-11 rounded-xl ${tone.iconBg} border ${tone.iconBorder} flex items-center justify-center mb-3 mt-4`}>
        <ProviderIcon providerId={provider.id} className={`w-5 h-5 ${tone.iconText}`} />
      </div>
      <h3 className="text-sm font-semibold text-white pr-16">{provider.label}</h3>
      <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2 min-h-[2rem]">{provider.description}</p>
      <p className="text-[10px] text-zinc-600 mt-2 uppercase tracking-wider">
        {connectVia === 'pipedream'
          ? 'Managed OAuth · no credentials needed'
          : (provider.auth_type === 'oauth2' ? 'OAuth2' : 'Credentials stored in vault')}
      </p>
    </button>
  );
};

const ConfigDrawer = ({ provider, connection, onClose, onSave, onDelete, saving, apiFetch, pipedreamStatus, onConnected }) => {
  const meta = getProviderMeta(provider?.id);
  const tone = CONNECTION_TONES[meta.tone] || CONNECTION_TONES.zinc;
  const [name, setName] = useState('');
  const [creds, setCreds] = useState({});

  useEffect(() => {
    if (!provider) return;
    setName(connection?.name || provider.label);
    const initial = {};
    (provider.fields || []).forEach((f) => {
      initial[f.key] = connection?.credentials?.[f.key] ?? '';
    });
    setCreds(initial);
  }, [provider, connection]);

  if (!provider) return null;

  const isOAuth = provider.auth_type === 'oauth2';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="conn-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <aside className="relative w-full max-w-md bg-zinc-950 border-l border-zinc-800 h-full overflow-y-auto flex flex-col shadow-2xl">
        <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl ${tone.iconBg} border ${tone.iconBorder} flex items-center justify-center`}>
                <ProviderIcon providerId={provider.id} className={`w-6 h-6 ${tone.iconText}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{provider.label}</h2>
                <p className="text-[11px] text-zinc-500">{provider.category} · {provider.auth_type}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-zinc-400 mt-3">{provider.description}</p>
        </div>

        <div className="flex-1 p-5 space-y-4">
          <ConnectionSetupGuide provider={provider} defaultOpen />

          {isOAuth ? (
            <OAuthConnectPanel
              provider={provider}
              connection={connection}
              apiFetch={apiFetch}
              onDelete={onDelete}
              onConnected={onConnected}
              pipedreamStatus={pipedreamStatus}
            />
          ) : (
            <>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Connection name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={provider.label}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
                  data-testid="conn-name"
                />
              </div>
              {(provider.fields || []).map((field) => (
                <div key={field.key}>
                  <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                    {field.label}
                    {field.required !== false && <span className="text-rose-400 ml-0.5">*</span>}
                  </label>
                  <input
                    type={field.secret ? 'password' : 'text'}
                    value={creds[field.key] ?? ''}
                    onChange={(e) => setCreds((c) => ({ ...c, [field.key]: e.target.value }))}
                    placeholder={field.placeholder || ''}
                    data-testid={`conn-field-${field.key}`}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
                  />
                </div>
              ))}
              <p className="text-[10px] text-zinc-600">
                Credentials are encrypted at rest and masked after saving. Automations use these when Marketing settings are empty.
              </p>
            </>
          )}
        </div>

        {!isOAuth && (
          <div className="sticky bottom-0 border-t border-zinc-800 bg-zinc-950 p-5 flex items-center gap-2">
            {connection && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(connection.id)}
                className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                data-testid="conn-delete"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Disconnect
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => onSave({ provider: provider.id, name, credentials: creds, connectionId: connection?.id })}
              disabled={saving}
              className="ml-auto bg-violet-600 hover:bg-violet-500"
              data-testid="conn-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              {connection ? 'Update' : 'Connect'}
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
};

const ConnectionsDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState(STATIC_CONNECTION_PROVIDERS);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawer, setDrawer] = useState({ provider: null, connection: null });
  const focusParam = (searchParams.get('focus') || '').trim().toLowerCase();
  const providerParam = (searchParams.get('provider') || '').trim().toLowerCase();
  const initialCategory = searchParams.get('category')
    || FOCUS_TO_CATEGORY[focusParam]
    || '';
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(initialCategory);
  const [categories, setCategories] = useState(CATEGORY_ORDER);
  const [catalogRows, setCatalogRows] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0); // full universe size (badge)
  const [pipedreamTotal, setPipedreamTotal] = useState(0);
  const [catalogMatchTotal, setCatalogMatchTotal] = useState(0); // filtered match count for pagination
  const [catalogHasMore, setCatalogHasMore] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [pipedreamStatus, setPipedreamStatus] = useState({ enabled: false });
  const [pipedreamSyncing, setPipedreamSyncing] = useState(false);
  const deepLinkApplied = useRef('');
  const pipedreamSyncedRef = useRef(false);

  const CATALOG_PAGE = 48;
  const configuredIds = useMemo(() => new Set(providers.map((p) => p.id)), [providers]);
  const configuredIdsRef = useRef(configuredIds);
  configuredIdsRef.current = configuredIds;
  const openConfigureRef = useRef(null);

  const token = localStorage.getItem('token');
  const API = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
    ? process.env.REACT_APP_BACKEND_URL
    : '';

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const apiFetch = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, { ...options, headers: { ...headers, ...(options.headers || {}) } });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const detail = data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  }, [API, headers]);

  const syncFromPipedream = useCallback(async ({ quiet = false } = {}) => {
    setPipedreamSyncing(true);
    try {
      const result = await apiFetch('/api/connections/pipedream/sync', { method: 'POST' });
      const count = Number(result?.synced_count) || (result?.synced || []).length || 0;
      if (!quiet) {
        if (count > 0) {
          toast.success(`Synced ${count} managed connection${count === 1 ? '' : 's'}`);
        } else {
          toast.message('No managed OAuth accounts to sync');
        }
      }
      return result;
    } catch (e) {
      if (!quiet) toast.error(e?.message || 'Connection sync failed');
      throw e;
    } finally {
      setPipedreamSyncing(false);
    }
  }, [apiFetch]);

  const load = useCallback(async ({ autoSyncPipedream = false } = {}) => {
    setLoading(true);
    try {
      let pd = { enabled: false };
      try {
        pd = await apiFetch('/api/connections/pipedream/status');
        setPipedreamStatus(pd || { enabled: false });
      } catch {
        setPipedreamStatus({ enabled: false });
      }

      // Heal Mongo when OAuth accounts already exist on Pipedream but /complete missed.
      if (autoSyncPipedream && pd?.enabled && !pipedreamSyncedRef.current) {
        pipedreamSyncedRef.current = true;
        try {
          await syncFromPipedream({ quiet: true });
        } catch {
          /* non-fatal — still load local connections */
        }
      }

      const clist = await apiFetch('/api/connections');
      setConnections(clist || []);
      try {
        const plist = await apiFetch('/api/connections/providers');
        setProviders(mergeProviderCatalog(plist));
      } catch {
        setProviders(STATIC_CONNECTION_PROVIDERS);
      }
      try {
        const cat = await apiFetch('/api/connections/categories');
        if (cat?.categories?.length) {
          setCategories([...new Set(cat.categories.map(displayCategoryLabel))]);
        }
      } catch {
        /* keep static category order */
      }
      try {
        const catalog = await apiFetch('/api/connections/providers/catalog?limit=1');
        if (catalog?.catalog_total) setCatalogTotal(catalog.catalog_total);
      } catch {
        /* optional */
      }
    } catch (e) {
      toast.error(e.message);
      setProviders(STATIC_CONNECTION_PROVIDERS);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, syncFromPipedream]);

  const loadCatalog = useCallback(async (offset = 0, append = false) => {
    setCatalogLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(CATALOG_PAGE), offset: String(offset) });
      if (search.trim()) params.set('q', search.trim());
      if (categoryFilter) params.set('category', categoryFilter);
      if (sourceFilter !== 'all') params.set('connect_via', sourceFilter);
      const data = await apiFetch(`/api/connections/providers/catalog?${params}`);
      const readyIds = configuredIdsRef.current;
      const rows = (data.providers || []).map((p) => ({
        ...p,
        availability: readyIds.has(p.id) ? 'ready' : 'catalog',
      }));
      setCatalogRows((prev) => {
        if (!append) return rows;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...rows.filter((p) => !seen.has(p.id))];
      });
      const universe = Number(data.catalog_total) || 0;
      const matchTotal = Number(data.total);
      const resolvedMatch = Number.isFinite(matchTotal) ? matchTotal : universe;
      if (universe > 0) setCatalogTotal(universe);
      if (Number(data.pipedream_total) > 0) setPipedreamTotal(Number(data.pipedream_total));
      setCatalogMatchTotal(resolvedMatch);
      const explicitHasMore = typeof data.has_more === 'boolean'
        ? data.has_more
        : (offset + rows.length) < resolvedMatch;
      setCatalogHasMore(explicitHasMore && rows.length > 0);
      if (data.categories?.length) {
        setCategories((prev) => {
          const merged = new Set([...prev, ...data.categories]);
          return Array.from(merged);
        });
      }
    } catch (e) {
      if (!append) {
        setCatalogRows([]);
        setCatalogHasMore(false);
      }
      toast.error(e.message || 'Catalog search failed');
    } finally {
      setCatalogLoading(false);
    }
  }, [apiFetch, search, categoryFilter, sourceFilter]);

  useEffect(() => {
    if (token) load({ autoSyncPipedream: true });
  }, [token, load]);

  useEffect(() => {
    if (!token) return undefined;
    const timer = setTimeout(() => loadCatalog(0, false), search.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search, categoryFilter, sourceFilter, token, loadCatalog]);

  useEffect(() => {
    const connected = searchParams.get('connected');
    if (!connected) return;
    const label = getOAuthProviderMeta(connected).label || connected;
    toast.success(`${label} is connected`);
    searchParams.delete('connected');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Connect Link success/error redirects land in the OAuth popup — notify opener and close.
  useEffect(() => {
    const connectedFlag = searchParams.get('pipedream_connected');
    const legacyFlag = searchParams.get('pipedream_oauth');
    if (!connectedFlag && !legacyFlag) return;

    const isSuccess =
      connectedFlag === '1'
      || connectedFlag === 'true'
      || legacyFlag === 'success';
    const isError =
      connectedFlag === '0'
      || connectedFlag === 'false'
      || legacyFlag === 'error';
    if (!isSuccess && !isError) return;

    const detail = formatPipedreamConnectError(
      extractPipedreamRedirectError(searchParams, window.location.hash || ''),
    );

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          {
            type: isSuccess ? 'pipedream-connect-success' : 'pipedream-connect-error',
            error: isError ? detail : undefined,
            app: searchParams.get('app') || undefined,
          },
          window.location.origin,
        );
      } catch {
        /* ignore */
      }
      window.close();
      return;
    }

    searchParams.delete('pipedream_connected');
    searchParams.delete('pipedream_oauth');
    searchParams.delete('app');
    setSearchParams(searchParams, { replace: true });
    if (isSuccess) {
      toast.success('Connect finished — syncing connections');
      if (token) {
        (async () => {
          try {
            await syncFromPipedream({ quiet: true });
          } catch {
            /* fall through to load */
          }
          await load();
        })();
      }
    } else if (isError) {
      toast.error(detail || 'Connect reported an error');
    }
  }, [searchParams, setSearchParams, token, load, syncFromPipedream]);

  // Apply readiness deep-links: ?category=Email&focus=email|social&provider=smtp
  useEffect(() => {
    const key = `${focusParam}|${searchParams.get('category') || ''}|${providerParam}`;
    if (deepLinkApplied.current === key) return;
    deepLinkApplied.current = key;
    const catFromUrl = searchParams.get('category') || FOCUS_TO_CATEGORY[focusParam] || '';
    if (focusParam === 'social') {
      setSourceFilter('all');
      // Prefer Marketing catalog (facebook/instagram/twitter); allow override via ?category=
      setCategoryFilter(catFromUrl || 'Marketing');
      setSearch('');
    } else if (catFromUrl) {
      setCategoryFilter(catFromUrl);
    }
  }, [focusParam, providerParam, searchParams]);

  const connectionByProvider = useMemo(() => {
    const map = {};
    connections.forEach((c) => {
      if (!map[c.provider]) map[c.provider] = c;
    });
    return map;
  }, [connections]);

  const connectedCount = useMemo(
    () => providers.filter((p) => connectionByProvider[p.id]).length,
    [providers, connectionByProvider],
  );

  const readyProviders = useMemo(
    () => providers.map((p) => ({
      ...p,
      availability: 'ready',
      configured: true,
    })),
    [providers],
  );

  const unifiedProviders = useMemo(() => {
    const byId = new Map();
    const byPdApp = new Map(); // pipedream_app slug → provider id

    const preferOver = (incoming, existing) => {
      if (!existing) return true;
      const aReady = incoming.availability === 'ready' ? 0 : 1;
      const bReady = existing.availability === 'ready' ? 0 : 1;
      if (aReady !== bReady) return aReady < bReady;
      const aSynth = String(incoming.id || '').startsWith('pipedream--') ? 1 : 0;
      const bSynth = String(existing.id || '').startsWith('pipedream--') ? 1 : 0;
      if (aSynth !== bSynth) return aSynth < bSynth;
      const aMcp = /[-_]mcp$/i.test(incoming.id || '') ? 1 : 0;
      const bMcp = /[-_]mcp$/i.test(existing.id || '') ? 1 : 0;
      if (aMcp !== bMcp) return aMcp < bMcp;
      return false;
    };

    const add = (raw) => {
      const p = scrubProviderForDisplay(raw);
      const sameId = byId.get(p.id);
      if (sameId && !preferOver(p, sameId)) return;

      const pd = (p.pipedream_app || '').trim().toLowerCase();
      if (pd && p.connect_via === 'pipedream') {
        const otherId = byPdApp.get(pd);
        if (otherId && otherId !== p.id) {
          const other = byId.get(otherId);
          if (!preferOver(p, other)) return;
          byId.delete(otherId);
        }
        byPdApp.set(pd, p.id);
      }

      byId.set(p.id, p);
    };

    readyProviders.forEach(add);
    catalogRows.forEach(add);
    return Array.from(byId.values());
  }, [readyProviders, catalogRows]);

  const filteredProviders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return unifiedProviders.filter((p) => {
      if (sourceFilter === 'pipedream' && p.connect_via !== 'pipedream') return false;
      if (sourceFilter === 'other' && p.connect_via === 'pipedream') return false;
      if (filter === 'connected' && !connectionByProvider[p.id]) return false;
      if (filter === 'available' && connectionByProvider[p.id]) return false;
      if (focusParam === 'social' && !SOCIAL_FOCUS_PROVIDER_IDS.has((p.id || '').toLowerCase())) {
        return false;
      }
      if (categoryFilter && (p.category || 'Other') !== categoryFilter) return false;
      if (q && p.availability === 'ready') {
        const hay = `${p.label} ${p.id} ${p.description} ${p.category}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [unifiedProviders, sourceFilter, filter, categoryFilter, search, connectionByProvider, focusParam]);

  const grouped = useMemo(() => {
    const groups = {};
    filteredProviders.forEach((p) => {
      const cat = p.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [filteredProviders]);

  const visibleCategories = useMemo(() => {
    const order = [...CATEGORY_ORDER];
    Object.keys(grouped).forEach((c) => { if (!order.includes(c)) order.push(c); });
    return order.filter((cat) => grouped[cat]?.length);
  }, [grouped]);

  const catalogOnlyCount = useMemo(
    () => filteredProviders.filter((p) => p.availability === 'catalog').length,
    [filteredProviders],
  );

  const canLoadMoreCatalog = catalogHasMore;

  const catalogRemaining = Math.max(0, catalogMatchTotal - catalogRows.length);

  const openConfigure = async (provider, connection) => {
    if (!provider?.id) return;
    const clickedId = provider.id;
    let resolved = resolveProviderEntry(provider);
    if (provider.availability === 'catalog' || provider.catalog_only || provider.source === 'snagcart_catalog') {
      try {
        const detail = await apiFetch(`/api/connections/providers/catalog/${encodeURIComponent(clickedId)}`);
        // Keep the list row's connect hints when the detail payload omits them —
        // the drawer gates the Pipedream Connect button on connect_via/pipedream_app.
        resolved = resolveProviderEntry({
          ...provider,
          ...detail,
          id: detail.id || clickedId,
          connect_via: detail.connect_via || provider.connect_via,
          pipedream_app: detail.pipedream_app || provider.pipedream_app,
        });
        resolved.availability = provider.availability || 'catalog';
      } catch {
        resolved = resolveProviderEntry(provider);
        resolved.availability = provider.availability || 'catalog';
      }
    } else {
      resolved.availability = 'ready';
    }
    // Guard against stale async responses if the user clicks another tile quickly
    if (resolved.id !== clickedId) {
      resolved = resolveProviderEntry(provider);
      resolved.availability = provider.availability || 'ready';
    }
    setDrawer({
      provider: resolved,
      connection: connection || connectionByProvider[clickedId] || null,
    });
  };
  openConfigureRef.current = openConfigure;

  // Open a specific provider drawer from ?provider=
  useEffect(() => {
    if (!providerParam || loading) return undefined;
    const hit = unifiedProviders.find((p) => (p.id || '').toLowerCase() === providerParam);
    if (!hit || !openConfigureRef.current) return undefined;
    const timer = setTimeout(() => {
      openConfigureRef.current(hit, connectionByProvider[hit.id] || null);
    }, 80);
    return () => clearTimeout(timer);
  }, [providerParam, loading, unifiedProviders, connectionByProvider]);

  const saveConnection = async ({ provider, name, credentials, connectionId }) => {
    setSaving(true);
    try {
      if (connectionId) {
        await apiFetch(`/api/connections/${connectionId}`, {
          method: 'PUT',
          body: JSON.stringify({ name, credentials }),
        });
        toast.success('Connection updated');
      } else {
        await apiFetch('/api/connections', {
          method: 'POST',
          body: JSON.stringify({ provider, name, credentials }),
        });
        toast.success('Connected');
      }
      await load();
      setDrawer({ provider: null, connection: null });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteConnection = async (id) => {
    if (!window.confirm('Disconnect this integration? Automations may stop working for this provider.')) return;
    try {
      await apiFetch(`/api/connections/${id}`, { method: 'DELETE' });
      toast.success('Disconnected');
      setDrawer({ provider: null, connection: null });
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-8 bg-zinc-950 text-white" data-testid="connections-dashboard">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate('/automations')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400 mt-0.5"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Plug className="w-6 h-6 text-violet-400" />
              Connections
            </h1>
            <p className="text-xs text-zinc-400 mt-1 max-w-xl">
              One search across {catalogTotal || providers.length} integrations
              {pipedreamTotal > 0 ? `, including ${pipedreamTotal} managed OAuth apps` : ''}.
              <span className="text-violet-300"> Managed OAuth</span> = one-click Connect, no client credentials ·
              <span className="text-amber-300"> BYOK</span> = your API key ·
              <span className="text-sky-300"> BYOC</span> = your own OAuth app.
              {canLoadMoreCatalog ? ' Use Load more at the bottom to browse the full catalog.' : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-center">
              <p className="text-lg font-bold text-emerald-300">{connectedCount}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Connected</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-center">
              <p className="text-lg font-bold text-zinc-200">{filteredProviders.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Showing</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-center hidden sm:block">
              <p className="text-lg font-bold text-sky-300">{catalogTotal > 0 ? catalogTotal : '—'}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Catalog</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {pipedreamStatus?.enabled && (
              <Button
                size="sm"
                variant="outline"
                disabled={pipedreamSyncing || loading}
                onClick={async () => {
                  try {
                    await syncFromPipedream({ quiet: false });
                    await load();
                  } catch {
                    /* toast already shown */
                  }
                }}
                className="h-9 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 whitespace-nowrap"
                data-testid="pipedream-sync-btn"
              >
                {pipedreamSyncing
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Plug className="w-3.5 h-3.5 mr-1.5" />}
                Sync connections
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/automations')}
              className="h-9 border-zinc-700 whitespace-nowrap"
            >
              <Workflow className="w-3.5 h-3.5 mr-1.5" /> Automations
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/ai-studio')}
              className="h-9 border-violet-500/40 text-violet-300 hover:bg-violet-500/10 whitespace-nowrap"
              data-testid="connections-ai-studio-btn"
            >
              <BrainCircuit className="w-3.5 h-3.5 mr-1.5" /> AI Studio
            </Button>
          </div>
        </div>
      </header>

      <div className="rounded-xl border border-sky-500/20 bg-sky-500/5 p-4 mb-6">
        <h2 className="text-sm font-semibold text-sky-200 mb-2">{CONNECTIONS_PAGE_INTRO.title}</h2>
        <ol className="list-decimal list-inside space-y-1 text-[11px] text-zinc-400 leading-relaxed">
          {CONNECTIONS_PAGE_INTRO.steps.map((step) => (
            <li key={step}>
              {step.split('**').map((part, i) => (
                i % 2 === 1 ? <strong key={`intro-${i}`} className="text-zinc-200">{part}</strong> : part
              ))}
            </li>
          ))}
        </ol>
        <p className="text-[10px] text-zinc-500 mt-2">
          Click any integration below for step-by-step setup instructions.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search all integrations — HubSpot, 3CX, Slack…"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
          data-testid="conn-search"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-300"
          data-testid="conn-category-filter"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {[
          { id: 'all', label: 'All providers' },
          { id: 'connected', label: 'Connected' },
          { id: 'available', label: 'Not connected' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setFilter(tab.id)}
            data-testid={`conn-filter-${tab.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              filter === tab.id
                ? 'bg-violet-500/15 border-violet-500/40 text-violet-200'
                : 'border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-6 flex-wrap items-center">
        <span className="text-[10px] text-zinc-600 uppercase tracking-wider mr-1">Connect with</span>
        {[
          { id: 'all', label: 'All methods' },
          { id: 'pipedream', label: `Managed OAuth${pipedreamTotal ? ` · ${pipedreamTotal}` : ''}` },
          { id: 'other', label: 'API keys & other' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSourceFilter(tab.id)}
            data-testid={`conn-source-${tab.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              sourceFilter === tab.id
                ? 'bg-sky-500/15 border-sky-500/40 text-sky-200'
                : 'border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
        <span className="text-[10px] text-zinc-600 ml-auto">
          {catalogLoading ? 'Updating catalog…' : `${filteredProviders.length} shown · ${catalogOnlyCount} catalog`}
        </span>
      </div>

      {loading && !filteredProviders.length ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-20 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading connections…
        </div>
      ) : filteredProviders.length === 0 ? (
        <p className="text-sm text-zinc-500 py-12 text-center">
          No matches — try another search or switch to <strong className="text-zinc-300">All methods</strong>.
        </p>
      ) : (
        <>
          {visibleCategories.map((category) => (
            <section key={category} className="mb-10" data-testid={`conn-category-${category.replace(/\s+/g, '-').toLowerCase()}`}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">{category}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {grouped[category].map((provider) => (
                  <ConnectionBlock
                    key={provider.id}
                    provider={provider}
                    connection={connectionByProvider[provider.id]}
                    onConfigure={openConfigure}
                  />
                ))}
              </div>
            </section>
          ))}
          {canLoadMoreCatalog && (
            <div className="sticky bottom-4 z-20 flex justify-center pb-6 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={catalogLoading}
                data-testid="conn-load-more-catalog"
                className="border-sky-500/40 bg-zinc-950/95 text-sky-200 hover:bg-sky-500/10 shadow-lg shadow-black/40"
                onClick={() => loadCatalog(catalogRows.length, true)}
              >
                {catalogLoading
                  ? 'Loading…'
                  : `Load more catalog${catalogRemaining > 0 ? ` (${catalogRemaining} remaining)` : ''}`}
              </Button>
            </div>
          )}
          {!canLoadMoreCatalog && catalogRows.length > 0 && !catalogLoading && (
            <p className="text-center text-[11px] text-zinc-600 pb-8">
              End of catalog results
              {catalogMatchTotal > 0 ? ` · ${catalogMatchTotal} matched` : ''}
              {catalogTotal > 0 ? ` · ${catalogTotal} total` : ''}.
              Clear filters or choose another connection method to browse more.
            </p>
          )}
        </>
      )}

      {drawer.provider && (
        <ConfigDrawer
          key={drawer.provider.id}
          provider={drawer.provider}
          connection={drawer.connection}
          onClose={() => setDrawer({ provider: null, connection: null })}
          onSave={saveConnection}
          onDelete={deleteConnection}
          saving={saving}
          apiFetch={apiFetch}
          pipedreamStatus={pipedreamStatus}
          onConnected={async () => {
            await load();
            setDrawer({ provider: null, connection: null });
          }}
        />
      )}
    </div>
  );
};

export default ConnectionsDashboard;
